class MenuLocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
      button: this.querySelector('.disclosure__button'),
      panel: this.querySelector('.disclosure__list-wrapper'),
      closeButton: this.querySelector('.menu-localization-panel__close'),
    };

    if (this.elements.button) {
      this.elements.button.addEventListener('click', this.openSelector.bind(this));
      this.elements.button.addEventListener('focusout', this.onFocusOut.bind(this));
      // The toggle is a div[role="button"] (not a real <button>), deliberately: MenuDrawer attaches a
      // blanket close-the-whole-drawer handler to every <button> inside it, which would otherwise fire
      // here too. A div needs its own keyboard activation for Enter/Space since it has no native button behavior.
      this.elements.button.addEventListener('keydown', this.onButtonKeyDown.bind(this));
    }

    if (this.elements.closeButton) {
      this.elements.closeButton.addEventListener('click', this.onCloseClick.bind(this));
      this.elements.closeButton.addEventListener('keydown', this.onCloseKeyDown.bind(this));
    }

    this.querySelectorAll('a[data-value]').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));

    this.boundOnDocumentClick = this.onDocumentClick.bind(this);
    document.addEventListener('click', this.boundOnDocumentClick);

    // Bound at the document level (not just `this`) because the panel is moved elsewhere in the DOM
    // below — keyup events inside it wouldn't otherwise bubble up to this custom element at all.
    this.boundOnContainerKeyUp = this.onContainerKeyUp.bind(this);
    document.addEventListener('keyup', this.boundOnContainerKeyUp);

    // Move the panel to be a direct child of the drawer's own content wrapper (menu-drawer__inner-container),
    // escaping .menu-drawer__localization's own positioning entirely. .menu-drawer has a CSS transform for
    // its slide-in animation, which makes it (not the viewport) the containing block for any position:fixed
    // descendant no matter how deeply nested — that broke earlier attempts to size this against the viewport
    // via JS-measured pixel coordinates. inner-container is stable (doesn't itself transform or transition),
    // already sized to exactly fill the drawer via plain CSS, so position:absolute + inset here just works,
    // no measurement or animation-timing races needed.
    if (this.elements.panel) {
      const innerContainer = document.querySelector('.menu-drawer__inner-container');
      if (innerContainer) innerContainer.appendChild(this.elements.panel);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.boundOnDocumentClick);
    document.removeEventListener('keyup', this.boundOnContainerKeyUp);
  }

  // True while the panel is open, or focus/click is anywhere inside the button or the panel — even
  // though the panel itself now lives elsewhere in the DOM (moved to menu-drawer__inner-container so
  // it can size itself against the whole drawer), not inside this custom element anymore.
  containsTarget(target) {
    if (this.contains(target)) return true;
    return !!(this.elements.panel && this.elements.panel.contains(target));
  }

  hidePanel() {
    if (!this.elements.button) return;
    this.elements.button.setAttribute('aria-expanded', 'false');
    if (this.elements.panel) this.elements.panel.classList.remove('menu-localization-panel--open');
  }

  onButtonKeyDown(event) {
    if (event.code !== 'Enter' && event.code !== 'Space') return;
    event.preventDefault();
    this.openSelector();
  }

  onCloseClick() {
    this.hidePanel();
    if (this.elements.button) this.elements.button.focus();
  }

  onCloseKeyDown(event) {
    if (event.code !== 'Enter' && event.code !== 'Space') return;
    event.preventDefault();
    this.onCloseClick();
  }

  onContainerKeyUp(event) {
    if (event.code !== 'Escape') return;
    if (!this.containsTarget(event.target)) return;
    this.hidePanel();
    this.elements.button.focus();
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.containsTarget(document.activeElement)) this.hidePanel();
    });
  }

  onDocumentClick(event) {
    if (!this.containsTarget(event.target)) this.hidePanel();
  }

  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    if (this.elements.input) this.elements.input.value = event.currentTarget.dataset.value;
    if (form) form.submit();
  }

  openSelector() {
    if (!this.elements.button) return;
    const expanded = this.elements.button.getAttribute('aria-expanded') === 'true';
    this.elements.button.setAttribute('aria-expanded', (!expanded).toString());
    if (this.elements.panel) this.elements.panel.classList.toggle('menu-localization-panel--open', !expanded);
  }
}

customElements.define('menu-localization-form', MenuLocalizationForm);
